import type { TrackerDocument, TrackerEnvelope } from '../domain/types';
import type {
  OnboardingInput,
  RoadmapSubject,
  SubjectPlanEnvelope,
  SubjectPlanSummary,
} from '../domain/platform';

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
  grade: number | null;
  curriculum: string | null;
  examTrack: string | null;
  onboardingCompleted: boolean;
  bannedAt: string | null;
  aiMonthlyLimit: number;
  isAdmin: boolean;
  leaderboardVisible: boolean;
  timezone: string;
  accentColor: string;
}

export interface AiAllowance {
  used: number;
  limit: number;
  remaining: number;
  period: string;
}

export interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
  createdAt?: string;
}

export interface AiHistory {
  threadId: string | null;
  messages: AiMessage[];
  allowance: AiAllowance;
}

export interface ProfileSettings {
  username: string;
  grade: 9 | 10 | 11 | 12;
  curriculum: 'CBSE' | 'ICSE' | 'ISC' | 'STATE' | 'IB' | 'OTHER';
  examTrack: 'SCHOOL' | 'BOARDS' | 'JEE' | 'NEET' | 'CUET' | 'OLYMPIAD' | 'OTHER';
  leaderboardVisible: boolean;
  timezone: string;
  accentColor: string;
}

export interface SettingsEnvelope {
  profile: ProfileSettings;
  ai: Pick<AiAllowance, 'used' | 'limit' | 'period'>;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  grade: number | null;
  points: number;
  completedSessions: number;
  streakDays: number;
  averageAccuracy: number | null;
}

export interface AdminOverview {
  users: number;
  onboardedUsers: number;
  bannedUsers: number;
  publicProfiles: number;
  activeUsers7d: number;
  subjectPlans: number;
  aiRequestsThisMonth: number;
  failedAiThisMonth: number;
  openReports: number;
  roadmapEntries: number;
  roadmapCorrections: number;
  totalPoints: number;
  completedSessions: number;
}

export interface AdminUserSummary {
  id: string;
  email: string;
  username: string | null;
  display_name: string;
  grade_level: number | null;
  curriculum: string | null;
  exam_track: string | null;
  onboarding_completed: number;
  banned_at: string | null;
  banned_reason: string | null;
  ai_monthly_limit: number;
  created_at: string;
  updated_at: string;
  plan_count: number;
  points: number;
}

export interface AdminReport {
  id: string;
  reporter_user_id: string;
  target_user_id: string;
  reporter_username: string | null;
  target_username: string | null;
  target_email: string;
  reason: string;
  details: string;
  status: 'pending' | 'reviewed' | 'actioned' | 'dismissed';
  admin_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminAuditAction {
  id: string;
  admin_email: string;
  target_user_id: string | null;
  action: string;
  details: string;
  created_at: string;
}

export interface AdminRoadmapFeedback {
  id: string;
  cache_key: string;
  feedback: string;
  created_at: string;
  username: string | null;
  email: string;
  subject_key: string | null;
  title: string | null;
  quality_status: 'active' | 'flagged' | null;
  reuse_count: number | null;
}

export interface AdminRoadmapEntry {
  cache_key: string;
  grade_level: number;
  curriculum: string;
  exam_track: string;
  subject_key: string;
  title: string;
  source_model: string;
  quality_status: 'active' | 'flagged';
  reuse_count: number;
  created_at: string;
  updated_at: string;
}

async function json<T>(response: Response): Promise<T> {
  const body = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new Error(body.error ?? `Request failed (${response.status})`);
  return body;
}

export async function loadSession(): Promise<SessionUser | null> {
  const response = await fetch('/api/session');
  return (await json<{ user: SessionUser | null }>(response)).user;
}

export async function loadTracker(): Promise<TrackerEnvelope> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return json<TrackerEnvelope>(
    await fetch('/api/tracker', {
      headers: { 'x-tarpy-timezone': timezone, 'x-tarpy-today': today },
    }),
  );
}

export async function completeOnboarding(input: OnboardingInput): Promise<void> {
  await json(
    await fetch('/api/onboarding', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function generateOnboardingSyllabus(input: {
  grade: 9 | 10 | 11 | 12;
  curriculum: ProfileSettings['curriculum'];
  examTrack: ProfileSettings['examTrack'];
  subjects: Array<{ key: string; title: string }>;
  forceRegenerate?: boolean;
  incorrectSubjectKeys?: string[];
  feedback?: string;
}): Promise<{
  subjects: RoadmapSubject[];
  model: string;
  source: 'cache' | 'mixed' | 'generated';
  firecrawlUsed: boolean;
  allowance: AiAllowance;
}> {
  return json(
    await fetch('/api/onboarding/syllabus', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function loadPlans(): Promise<SubjectPlanSummary[]> {
  return (await json<{ plans: SubjectPlanSummary[] }>(await fetch('/api/plans'))).plans;
}

export async function loadPlan(planId: string): Promise<SubjectPlanEnvelope> {
  return json(await fetch(`/api/plans/${encodeURIComponent(planId)}`));
}

export async function savePlan(
  planId: string,
  version: number,
  document: TrackerDocument,
): Promise<{ version: number; updatedAt: string }> {
  return json(
    await fetch(`/api/plans/${encodeURIComponent(planId)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version, document }),
    }),
  );
}

export async function updatePlanSettings(
  planId: string,
  autoRebalance: boolean,
): Promise<{ autoRebalance: boolean; updatedAt: string }> {
  return json(
    await fetch(`/api/plans/${encodeURIComponent(planId)}/settings`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ autoRebalance }),
    }),
  );
}

export async function loadSettings(): Promise<SettingsEnvelope> {
  return json(await fetch('/api/settings'));
}

export async function saveSettings(profile: ProfileSettings): Promise<{ updatedAt: string }> {
  return json(
    await fetch('/api/settings', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(profile),
    }),
  );
}

export async function exportAccountData(): Promise<Blob> {
  const response = await fetch('/api/account/export');
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Export failed (${response.status})`);
  }
  return response.blob();
}

export async function deleteAccount(): Promise<void> {
  await json(await fetch('/api/account', { method: 'DELETE' }));
}

export async function loadLeaderboard(): Promise<LeaderboardEntry[]> {
  return (await json<{ entries: LeaderboardEntry[] }>(await fetch('/api/leaderboard'))).entries;
}

export async function reportLearner(input: {
  targetUserId: string;
  reason: 'cheating' | 'harassment' | 'impersonation' | 'other';
  details: string;
}): Promise<void> {
  await json(
    await fetch('/api/reports', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function loadAiHistory(planId: string): Promise<AiHistory> {
  return json(await fetch(`/api/ai/history?planId=${encodeURIComponent(planId)}`));
}

export async function sendAiMessage(input: {
  threadId?: string;
  planId: string;
  message: string;
  useWeb: boolean;
}): Promise<{
  threadId: string;
  message: AiMessage;
  model: string;
  firecrawlUsed: boolean;
  allowance: AiAllowance;
}> {
  return json(
    await fetch('/api/ai/chat', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function loadAdminOverview(): Promise<AdminOverview> {
  return json(await fetch('/api/admin/overview'));
}

export async function loadAdminUsers(query = ''): Promise<AdminUserSummary[]> {
  const result = await json<{ users: AdminUserSummary[] }>(
    await fetch(`/api/admin/users?q=${encodeURIComponent(query)}`),
  );
  return result.users;
}

export async function loadAdminUser(userId: string): Promise<{
  user: Record<string, unknown>;
  plans: Array<Record<string, unknown>>;
  usage: Array<Record<string, unknown>>;
  reports: Array<Record<string, unknown>>;
  actions: Array<Record<string, unknown>>;
}> {
  return json(await fetch(`/api/admin/users/${encodeURIComponent(userId)}`));
}

export async function updateAdminUser(
  userId: string,
  input: { banned?: boolean; banReason?: string; aiMonthlyLimit?: number },
): Promise<void> {
  await json(
    await fetch(`/api/admin/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function loadAdminReports(): Promise<AdminReport[]> {
  return (await json<{ reports: AdminReport[] }>(await fetch('/api/admin/reports'))).reports;
}

export async function updateAdminReport(
  reportId: string,
  input: { status: 'reviewed' | 'actioned' | 'dismissed'; note: string },
): Promise<void> {
  await json(
    await fetch(`/api/admin/reports/${encodeURIComponent(reportId)}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  );
}

export async function loadAdminAudit(): Promise<AdminAuditAction[]> {
  return (await json<{ actions: AdminAuditAction[] }>(await fetch('/api/admin/audit'))).actions;
}

export async function loadAdminRoadmaps(): Promise<{
  feedback: AdminRoadmapFeedback[];
  entries: AdminRoadmapEntry[];
}> {
  return json(await fetch('/api/admin/roadmaps'));
}

export async function saveTracker(
  version: number,
  document: TrackerDocument,
): Promise<{ version: number; updatedAt: string }> {
  return json(
    await fetch('/api/tracker', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version, document }),
    }),
  );
}

export async function logout(): Promise<void> {
  await json(await fetch('/api/auth/logout', { method: 'POST' }));
}
